import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IndosbookLogo } from "./IndosbookLogo";
import { useAuth, api } from "../context/AuthContext";
import { PhotoNudge, PhotoNudgeText } from "./PhotoNudge";
import { ProfileCompleteness } from "./ProfileCompleteness";
import ApplicationsTracker from "./ApplicationsTracker";
import NotificationsBell from "./NotificationsBell";
import RightSidebar from "./RightSidebar";
import HeaderSearch from "./HeaderSearch";
import PostCard from "./PostCard";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Image as ImageIcon, Smile, FileText, Award, ChevronRight,
  LogOut, User as UserIcon, BadgeCheck, Navigation, Building2, Briefcase, Search, X, ChevronDown,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const AVAIL = {
  ready: { label: "Ready to Board", dot: "bg-green-500", pill: "bg-green-50 text-green-700" },
  at_sea: { label: "At Sea", dot: "bg-slate-500", pill: "bg-slate-100 text-slate-700" },
  on_leave: { label: "On Leave", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700" },
};

const daysLeft = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);

const FeedPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const name = user?.name || "Seafarer";
  const isCompany = user?.role === "company";
  const rank = user?.rank || user?.job_title || (isCompany ? "Shipping Company" : user?.role === "institute" ? "Maritime Institute" : "Seafarer");
  const org = user?.organization || "Indosbook Member";
  const initial = name.charAt(0).toUpperCase();
  const avail = AVAIL[user?.availability] || null;
  const avatarSrc = user?.avatar_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${user.avatar_path}` : user?.picture || null;
  const [mobileSearch, setMobileSearch] = useState(false);

  const [posts, setPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [certs, setCerts] = useState([]);
  const [profileCounts, setProfileCounts] = useState(null);
  const [composer, setComposer] = useState("");
  const [apps, setApps] = useState([]);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [postsSignal, setPostsSignal] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [richOpen, setRichOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [voyage, setVoyage] = useState({ vessel: "", vessel_type: "", route: "", rank: "" });

  const loadFeed = useCallback(async () => {
    try { setPosts((await api.get("/posts/feed?tab=all")).data.posts); } catch { /* ignore */ } finally { setFeedLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/applications").then((r) => setApps(r.data.applications)).catch(() => {});
    loadFeed();
  }, [loadFeed, postsSignal]);

  useEffect(() => {
    api.get("/profile").then((r) => {
      setCerts((r.data.certificates || []).filter((c) => c.expiry_date));
      setProfileCounts({ sea: (r.data.sea_service || []).length, certs: (r.data.certificates || []).length });
    }).catch(() => {});
  }, []);

  const appliedIds = useMemo(() => new Set(apps.map((a) => a.job_post_id)), [apps]);

  const applyJob = async (p) => {
    try {
      const payload = {
        job_post_id: p.id, job_title: p.title, company: p.author,
        rank: p.job?.rank, vessel: p.job?.vessel, salary: p.job?.salary, location: p.job?.location,
      };
      const created = (await api.post("/applications", payload)).data;
      setApps((prev) => [created, ...prev]);
      toast({ title: "Application submitted!", description: `${p.job?.rank} · ${p.author}` });
    } catch (e) {
      const dup = e?.code === 409 || e?.response?.status === 409;
      toast({
        title: dup ? "Already applied" : "Could not apply",
        description: dup ? "Track it from the applications card." : e?.response?.data?.detail,
        variant: dup ? undefined : "destructive",
      });
    }
  };

  const onVote = async (id) => {
    try {
      const res = (await api.post(`/posts/${id}/vote`)).data;
      setPosts((list) => list.map((p) => (p.id === id ? { ...p, upvoted: res.upvoted, upvotes: res.upvotes } : p)));
    } catch { /* ignore */ }
  };

  const post = async () => {
    if (!composer.trim()) { toast({ title: "Write something to share first." }); return; }
    const hasVoyage = voyage.vessel || voyage.route || voyage.rank;
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("title", composer.trim());
      fd.append("community", "SeaJobs");
      if (hasVoyage) fd.append("voyage", JSON.stringify(voyage));
      if (imageFile) fd.append("image", imageFile);
      const created = (await api.post("/posts", fd)).data;
      setPosts((list) => [created, ...list]);
      setComposer(""); setImageFile(null); setVoyage({ vessel: "", vessel_type: "", route: "", rank: "" }); setRichOpen(false);
      setPostsSignal((s) => s + 1);
      toast({ title: "Posted to your feed!" });
    } catch (e) {
      toast({ title: "Could not post", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setPosting(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };
  const soon = (t) => toast({ title: t, description: "Coming soon in the next update." });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ===== Top bar ===== */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
        <div className="mx-auto grid h-16 max-w-[1180px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 px-4 sm:px-6 lg:grid-cols-[300px_minmax(0,1fr)_360px] lg:gap-5">
          <div className="flex items-center">
            <button onClick={() => navigate("/feed")} className="shrink-0">
              <IndosbookLogo size={28} />
            </button>
          </div>
          <div className="hidden md:block">
            <HeaderSearch />
          </div>
          <div className="col-start-3 flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setMobileSearch((v) => !v)}
              aria-label={mobileSearch ? "Close search" : "Search"}
              data-testid="mobile-search-toggle"
              className={`rounded-full p-2 transition-colors md:hidden ${mobileSearch ? "bg-[#FF4500] text-white" : "text-gray-500 hover:bg-gray-100"}`}
            >
              {mobileSearch ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
            </button>
            <div className="lg:flex lg:flex-1 lg:justify-center" data-testid="header-bell-slot">
              <NotificationsBell />
            </div>
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="header-profile-button" className="flex items-center gap-2 rounded-full border border-gray-200 py-1 pl-1 pr-3 transition-colors hover:bg-gray-50 lg:w-[240px]">
                {avatarSrc ? (
                  <img src={avatarSrc} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" data-testid="header-avatar" />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-700" data-testid="header-avatar">{initial}</span>
                )}
                <span className="hidden min-w-0 flex-1 text-left sm:block">
                  <span className="flex items-center gap-1 text-sm font-bold text-gray-800"><span className="truncate">{name}</span> <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-[#002855]" /></span>
                  <span className="block truncate text-xs capitalize text-gray-500">{rank}</span>
                </span>
                <ChevronDown className="hidden h-4 w-4 shrink-0 text-gray-400 lg:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-bold">{name}</div>
                <div className="text-xs font-normal capitalize text-gray-500">{user?.role} · {rank}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-item" className="cursor-pointer"><UserIcon className="mr-2 h-4 w-4" /> Profile</DropdownMenuItem>
              {user?.role === "company" && (
                <DropdownMenuItem onClick={() => navigate("/company")} data-testid="company-dashboard-menu-item" className="cursor-pointer"><Building2 className="mr-2 h-4 w-4" /> Company Dashboard</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600"><LogOut className="mr-2 h-4 w-4" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
        {mobileSearch && (
          <div className="border-t border-gray-100 px-4 py-3 md:hidden" data-testid="mobile-search-row">
            <HeaderSearch />
          </div>
        )}
      </header>

      <div className="mx-auto grid max-w-[1180px] grid-cols-1 justify-center gap-5 px-4 py-6 sm:px-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* ===== Left column ===== */}
        <aside className="space-y-5">
          {/* Profile card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center" data-testid="feed-profile-card">
            <div className="relative mx-auto h-20 w-20">
              {avatarSrc ? (
                <img src={avatarSrc} alt="" className="h-20 w-20 rounded-full object-cover" />
              ) : (
                <PhotoNudge initial={initial} />
              )}
              {avail && avatarSrc && <span className={`absolute bottom-1 right-1 h-4 w-4 rounded-full border-2 border-white ${avail.dot}`} />}
            </div>
            {!avatarSrc && <PhotoNudgeText />}
            <h2 className="mt-3 text-xl font-extrabold text-gray-900" data-testid="feed-profile-name">{name}</h2>
            <p className="mt-0.5 text-sm font-semibold capitalize text-gray-600">{rank}</p>
            <p className="text-xs text-gray-400">{org}</p>
            {avail && (
              <span className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${avail.pill}`} data-testid="feed-availability">
                <span className={`h-2 w-2 rounded-full ${avail.dot}`} /> {avail.label}
              </span>
            )}
            {user?.role === "seafarer" && profileCounts && (
              <ProfileCompleteness onOpen={() => navigate("/profile")} items={[
                { key: "photo", label: "Photo", done: !!avatarSrc },
                { key: "rank", label: "Rank", done: !!user?.rank },
                { key: "sea", label: "Sea service", done: profileCounts.sea > 0 },
                { key: "certs", label: "Certificates", done: profileCounts.certs > 0 },
              ]} />
            )}
            <button onClick={() => navigate("/profile")} data-testid="update-sea-service-button" className="mt-4 w-full rounded-full border border-slate-300 bg-white py-2.5 text-sm font-bold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-100">
              {user?.role === "seafarer" ? "Update Sea Service Record" : "Edit Profile"}
            </button>
          </div>

          {isCompany ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="feed-company-card">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100"><Briefcase className="h-5 w-5 text-slate-600" /></span>
                <div>
                  <div className="font-bold text-gray-900">Hire verified crew</div>
                  <div className="text-xs text-gray-500">Post vacancies and manage applicants</div>
                </div>
              </div>
              <button onClick={() => navigate("/company")} data-testid="open-company-dashboard-button" className="mt-4 flex w-full items-center justify-center gap-1 rounded-full bg-[#FF4500] py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#e03d00]">
                Open Company Dashboard <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              {/* Applications card */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100"><FileText className="h-5 w-5 text-slate-600" /></span>
                  <div>
                    <div className="font-bold text-gray-900">Track your Maritime Applications</div>
                    <div className="text-xs text-gray-500" data-testid="applications-count">
                      {apps.length} active application{apps.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                <button onClick={() => setTrackerOpen(true)} data-testid="track-status-button" className="mt-4 flex w-full items-center justify-center gap-1 rounded-full border border-gray-200 bg-white py-2.5 text-sm font-bold text-gray-800 transition-colors hover:border-slate-300 hover:bg-slate-50">
                  Track Status <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* STCW card */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="stcw-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-bold text-gray-900"><Award className="h-5 w-5 text-slate-600" /> STCW Expiry Monitor</div>
                  <span className="text-xs text-gray-400" data-testid="stcw-count">{certs.length} Docs</span>
                </div>
                <div className="mt-3 space-y-2">
                  {certs.length === 0 && (
                    <p className="rounded-lg border border-dashed border-gray-200 p-3 text-center text-xs text-gray-400" data-testid="stcw-empty">
                      Add your certificates to get expiry alerts.
                    </p>
                  )}
                  {certs.slice(0, 3).map((c) => {
                    const d = daysLeft(c.expiry_date);
                    const tone = d < 0 ? "border-red-100 bg-red-50/60" : d <= 60 ? "border-amber-100 bg-amber-50/60" : "border-gray-100 bg-gray-50/60";
                    const dot = d < 0 ? "bg-red-500" : d <= 60 ? "bg-amber-500" : "bg-green-500";
                    return (
                      <div key={c.id} className={`rounded-lg border p-3 ${tone}`} data-testid={`stcw-cert-${c.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-gray-800">{c.name}</div>
                            <div className="text-xs text-gray-500">{d < 0 ? `Expired ${-d} days ago` : `Expires in ${d} days`}</div>
                          </div>
                          <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${dot}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => navigate("/profile#vault")} data-testid="open-certificate-vault-button" className="mt-3 flex w-full items-center justify-center gap-1 rounded-full bg-gray-50 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-100">
                  Open Certificate Vault <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </aside>

        {/* ===== Main column (Facebook-width feed) ===== */}
        <main className="mx-auto w-full min-w-0 max-w-[640px] lg:max-w-none">
          {/* Composer */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-3">
              {avatarSrc ? (
                <img src={avatarSrc} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" data-testid="composer-avatar" />
              ) : (
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sm font-bold text-[#FF4500]" data-testid="composer-avatar">{initial}</span>
              )}
              <input
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && post()}
                placeholder={`What's on your mind, ${name.split(" ")[0]}?`}
                data-testid="composer-input"
                className="h-11 flex-1 rounded-full bg-gray-100 px-5 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
            {imageFile && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-gray-50 p-2 text-sm" data-testid="composer-image-chip">
                <ImageIcon className="h-4 w-4 text-slate-400" />
                <span className="truncate text-gray-600">{imageFile.name}</span>
                <button onClick={() => setImageFile(null)} className="ml-auto text-gray-400 hover:text-red-500">✕</button>
              </div>
            )}
            {richOpen && (
              <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="composer-voyage">
                <div className="col-span-2 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-slate-500"><Navigation className="h-3.5 w-3.5" /> Voyage Card</div>
                <input value={voyage.vessel} onChange={(e) => setVoyage({ ...voyage, vessel: e.target.value })} placeholder="Vessel name" data-testid="voyage-vessel" className="h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none" />
                <input value={voyage.vessel_type} onChange={(e) => setVoyage({ ...voyage, vessel_type: e.target.value })} placeholder="Vessel type" data-testid="voyage-type" className="h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none" />
                <input value={voyage.route} onChange={(e) => setVoyage({ ...voyage, route: e.target.value })} placeholder="Route (e.g. Rotterdam → Singapore)" data-testid="voyage-route" className="h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none" />
                <input value={voyage.rank} onChange={(e) => setVoyage({ ...voyage, rank: e.target.value })} placeholder="Your rank" data-testid="voyage-rank" className="h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none" />
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3">
              <label className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-gray-600 hover:bg-gray-100" data-testid="composer-photo-label">
                <ImageIcon className="h-5 w-5 text-slate-400" /> Photo
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="composer-photo-input"
                  onChange={(e) => { setImageFile(e.target.files?.[0] || null); e.target.value = ""; }} />
              </label>
              <button onClick={() => setRichOpen((s) => !s)} data-testid="composer-voyage-toggle" className={`flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold hover:bg-gray-100 ${richOpen ? "text-[#002855]" : "text-gray-600"}`}><Navigation className="h-5 w-5 text-slate-400" /> Voyage</button>
              <button onClick={() => soon("Feeling")} className="hidden h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-gray-600 hover:bg-gray-100 sm:flex"><Smile className="h-5 w-5 text-slate-400" /> Feeling</button>
              <Button onClick={post} disabled={posting} data-testid="post-button" className="ml-auto h-9 rounded-full bg-[#FF4500] px-6 text-sm font-bold text-white hover:bg-[#e03d00]">{posting ? "Posting…" : "Post"}</Button>
            </div>
          </div>

          <RightSidebar refreshPostsSignal={postsSignal} />

          {/* Posts */}
          <div className="mt-4 space-y-4">
            {!feedLoading && posts.length === 0 && (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center" data-testid="feed-empty">
                <p className="text-base font-bold text-gray-800">Your feed is quiet — for now</p>
                <p className="mt-1 text-sm text-gray-500">
                  Share your first post above, follow communities or add friends to fill your feed.
                </p>
              </div>
            )}
            {posts.map((p) => (
              <PostCard key={p.id} post={p} score={p.upvotes} upvoted={p.upvoted}
                onVote={onVote} onApply={applyJob} applied={appliedIds.has(p.id)} />
            ))}
          </div>
        </main>

      </div>

      <ApplicationsTracker open={trackerOpen} onOpenChange={setTrackerOpen} />
    </div>
  );
};

export default FeedPage;
